import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { faqData } from './consts'
import HeroSection from '../../../shared/Rewrite/BrandPages/HeroSection'
import Accordion from 'src/components/shared/FAQ/Accordion'
import PrimaryButton from 'src/components/shared/PrimaryButton'
import RecommendedReads from '../../../shared/BrandPages/RecommendedReads'
import BottomSection from '../../../shared/BrandPages/BottomSection'
import Section from '../../../shared/BrandPages/Section'
import ArrowIcon from '../../../shared/ArrowIcon'
import Content from '../../../shared/BrandPages/Content'
import { APPLY_FDR_URL } from '../../../../common/constants'
import styles from './index.module.scss'
import { useLeadId } from '../../../../common/lead-id'
import useDeviceDetect from '../../../../common/hooks/useDeviceDetect'
import { useRouter } from 'next/router'
import { buildQueryString } from '../../../../common/utils'
import WithSeoOverrides from 'src/components/shared/WithSeoOverride'
import { useTrustMarkers } from 'src/common/trust-markers'
import { numberFormat } from 'src/common/utils'

const Faq = ({ faqs, showScheduleACallButton }) => {
  const { seo, heroData, recommendedReads, bottomSectionData } = faqData
  const [faqItemsCount, setFaqItemsCount] = useState(10)
  const [faqItems, setFaqItems] = useState(faqs)
  const [text, setText] = useState('Show More')
  const [rotate, setRotate] = useState('rotate-180')
  const faqAccordionRef = useRef(null)
  const { isMobile } = useDeviceDetect()
  const faqsCount = faqs.length
  const leadId = useLeadId()
  const { query: baseQuery } = useRouter()
  const { getTrustMarkerById } = useTrustMarkers()
  const trustPilot = getTrustMarkerById('truspilot')
  let query = useMemo(
    () => buildQueryString(baseQuery, leadId),
    [baseQuery, leadId]
  )

  const url = `${APPLY_FDR_URL()}?${query}`

  const handleClick = useCallback(() => {
    if (faqItemsCount >= faqsCount) {
      setFaqItemsCount(10)
      setText('Show More')
      setRotate('rotate-180')
      faqAccordionRef.current.scrollIntoView()
    } else {
      const count = faqItemsCount + 10
      setFaqItemsCount(count)
      if (count >= faqsCount) {
        setText('Show Less')
      }
    }
  }, [faqItemsCount, faqsCount])

  useEffect(() => {
    const faqItemsToShow = faqs.slice(0, faqItemsCount)
    setFaqItems(faqItemsToShow)
  }, [faqItemsCount, faqs])

  return (
    <Content>
      <WithSeoOverrides {...seo} />
      <Section classNames="pt-0 pb-0 lg:pl-4 xl:pl-0">
        <HeroSection
          {...heroData}
          contentClassName={styles.contentClassName}
          heroContainerClassName={styles.heroClassName}
          showNewTrustpilot
          trustPilotInfo={{
            rating: Number(trustPilot?.rating),
            totalReviews: numberFormat(Number(trustPilot?.totalReview)),
            link: trustPilot?.link,
            color: '#33b083',
          }}
          showScheduleACallButton={showScheduleACallButton}
        />
      </Section>
      <Section classNames="bg-blue-base-white">
        <div ref={faqAccordionRef} className="mt-9 lg:mt-0">
          <Accordion
            titleCenter
            titleClassName="text-37 font-bold my-0 lg:my-0 leading-52"
            questionClassName="font-medium lg:pt-5.4"
            answerClassName="pl-4 pr-12 text-15 lg:pl-0"
            items={faqItems}
            title="What you should know"
          />
        </div>
        <div className="text-center text-blue-base">
          <div className="my-primary mb-0 flex cursor-pointer flex-row justify-center lg:mb-10">
            <b onClick={handleClick}>{text}</b>
            <ArrowIcon stroke="#041B93" className={rotate} />
          </div>
          {isMobile ? null : (
            <PrimaryButton
              title="Get started"
              sectionTitle="What you should know"
              href={url}
              className="bg-red-850 px-9 py-2.5 text-white"
            />
          )}
        </div>
      </Section>
      <Section>
        <RecommendedReads data={recommendedReads} />
      </Section>
      <BottomSection {...bottomSectionData} />
    </Content>
  )
}

Faq.propTypes = {
  faqs: PropTypes.array.isRequired,
  showScheduleACallButton: PropTypes.bool,
}

export default Faq
